import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import type { EntryOptions, EntryRound } from '../../types/trophy';
import { allTurnsFull, choosesTurn, chosenTurn } from './entry-options';

interface EntryOptionsSummaryProps {
  options: EntryOptions;
  /** The turn picked; one that is not among the class's turns counts as none. */
  roundId: string | null;
  onRoundChange: (roundId: string) => void;
  /** A refusal about the turn, already in words. */
  turnError?: string | null;
  /** The radios' group name — distinct per summary on a page that shows several. */
  name?: string;
  /** How a class kept as it is (`kept`) is explained; "as at earlier stages" by default. */
  keptHint?: string;
}

/**
 * What an entry will be, before anybody confirms it: the **category** the
 * organiser's strategy puts the athlete in (or why there is none yet), and the
 * **rounds** of that category with their day and time. When the category is
 * split over two or more turns, and the organiser's agreement lets an entry
 * book one, the turn is picked here; otherwise the turns are listed, and the
 * rest of the day after them, so the athlete sees how long it is.
 *
 * A turn with a limit says how many seats it has left; a full one stays in
 * the list but cannot be picked (unless it is the one already booked), and
 * when every turn is full the summary says so instead of asking for one.
 *
 * The one picture of it in the app, shared by the athlete's own entry dialog
 * (EnterStageDialog), the team manager's (TeamEntryDialog) and the guided
 * sign-up of a parent: all three read the same answer from the server
 * (EntryOptions), so they say the same thing in the same words.
 */
export default function EntryOptionsSummary({
  options,
  roundId,
  onRoundChange,
  turnError = null,
  name = 'entry-turn',
  keptHint,
}: EntryOptionsSummaryProps) {
  const { t, i18n } = useTranslation();
  const bookable = options.bookable_rounds;
  const choosing = choosesTurn(options);
  const chosen = chosenTurn(options, roundId);

  const roundTime = (round: EntryRound) => {
    if (!round.start_time) return t('stageEntry.dialog.timeTba');
    return new Date(round.start_time).toLocaleString(i18n.language, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const roundLine = (round: EntryRound) => t('stageEntry.dialog.roundLine', { name: round.name, time: roundTime(round) });

  /** The seats left on a turn with a limit — nothing for one without. */
  const seatsNote = (round: EntryRound) => {
    if (round.full) return t('stageEntry.dialog.full');
    if (round.seats_left == null) return null;
    return t('stageEntry.dialog.seatsLeft', { count: round.seats_left });
  };

  const seatsBadge = (round: EntryRound) => {
    const note = seatsNote(round);
    if (!note) return null;
    return (
      <span
        className={cn('shrink-0 text-xs', round.full ? 'text-destructive' : 'text-muted-foreground')}
        data-testid="turn-seats"
      >
        {note}
      </span>
    );
  };

  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
      <dt className="text-muted-foreground">{t('stageEntry.dialog.category')}</dt>
      <dd className="min-w-0" data-testid="entry-category" data-state={options.category_state}>
        {options.category ? (
          <>
            <span className="font-medium">{options.category.name}</span>
            {(options.category_state === 'assigned' || options.category_state === 'kept') && (
              <span className="text-muted-foreground">
                {' · '}
                {options.category_state === 'kept'
                  ? keptHint ?? t('stageEntry.dialog.categoryKept')
                  : t('stageEntry.dialog.categoryAssigned')}
              </span>
            )}
          </>
        ) : options.category_state === 'needs_data' ? (
          <span className="text-muted-foreground">{t('stageEntry.dialog.categoryNeedsData')}</span>
        ) : (
          <span className="text-muted-foreground">{t('stageEntry.dialog.categoryByOrganiser')}</span>
        )}
      </dd>

      {options.category && bookable.length > 0 && (
        <>
          <dt className="text-muted-foreground">
            {t(bookable.length > 1 && !choosing ? 'stageEntry.dialog.turns' : 'stageEntry.dialog.turn')}
          </dt>
          <dd className="min-w-0" data-testid="entry-turns">
            {choosing ? (
              <div className="space-y-1.5" role="radiogroup" aria-label={t('stageEntry.dialog.turn')}>
                {bookable.map((round) => {
                  // The turn already picked stays pickable even once full: it
                  // may be the entry's own booking, which holds one of its seats.
                  const closed = !!round.full && chosen !== round.id;
                  return (
                    <label
                      key={round.id}
                      className={cn(
                        'flex items-center gap-2 rounded-md border px-3 py-1.5',
                        closed ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                        chosen === round.id && 'border-primary ring-1 ring-primary'
                      )}
                      data-testid="turn-option"
                      data-full={round.full ? 'true' : undefined}
                    >
                      <input
                        type="radio"
                        name={name}
                        value={round.id}
                        checked={chosen === round.id}
                        disabled={closed}
                        onChange={() => onRoundChange(round.id)}
                        className="accent-primary"
                      />
                      <span className="min-w-0 flex-1 break-words">{roundLine(round)}</span>
                      {seatsBadge(round)}
                    </label>
                  );
                })}
              </div>
            ) : (
              <ul className="space-y-0.5">
                {bookable.map((round) => (
                  <li key={round.id} className="flex items-baseline gap-2 break-words">
                    <span className="min-w-0 flex-1">{roundLine(round)}</span>
                    {seatsBadge(round)}
                  </li>
                ))}
              </ul>
            )}
            {/* Turns the organiser's agreement does not let an entry book: the
                athlete is on every one, and the desk says which. */}
            {bookable.length > 1 && !choosing && (
              <p className="text-xs text-muted-foreground">{t('stageEntry.dialog.turnByOrganiser')}</p>
            )}
            {allTurnsFull(options) && !chosen && (
              <p className="text-xs text-destructive" data-testid="entry-turns-full">
                {t('stageEntry.dialog.allTurnsFull')}
              </p>
            )}
            {turnError && (
              <p className="text-xs text-destructive" data-testid="entry-turn-error">
                {turnError}
              </p>
            )}
          </dd>
        </>
      )}

      {options.category && options.other_rounds.length > 0 && (
        <>
          <dt className="text-muted-foreground">
            {t(bookable.length > 0 ? 'stageEntry.dialog.then' : 'stageEntry.dialog.rounds')}
          </dt>
          <dd className="min-w-0" data-testid="entry-other-rounds">
            <ul className="space-y-0.5">
              {options.other_rounds.map((round) => (
                <li key={round.id} className="break-words">{roundLine(round)}</li>
              ))}
            </ul>
          </dd>
        </>
      )}
    </dl>
  );
}
